/**
 * CandidateManager — Extracted from App class.
 * Handles all candidate CRUD, resume parsing, scoring, interview, and hiring status.
 */
import { AppEventType } from './appEvents';
import { CandidateCreationSourceType, CandidateState, advanceStageForCapability, StageCapability, nowUtc, } from '@common/clientServerApi';
export class CandidateManager {
    constructor(deps) {
        this.deps = deps;
    }
    get api() { return this.deps.getApi(); }
    get cache() { return this.deps.dataCache; }
    get events() { return this.deps.eventEmitter; }
    // ===============================
    // CRUD
    // ===============================
    getFromCache() {
        return this.cache.getCandidates() || [];
    }
    removeFromCache(candidateId) {
        this.cache.removeCandidate(candidateId);
    }
    async getById(id) {
        try {
            const cached = this.cache.getCandidate(id);
            if (cached)
                return cached;
            const response = await this.deps.makeApiCallWithRetry(() => this.api.getCandidate(id));
            return response.candidate;
        }
        catch (error) {
            this.deps.handleError('getCandidate', this.deps.toError(error));
            throw error;
        }
    }
    /**
     * Unified candidate creation. Accepts any combination of:
     * - candidateData: manual fields
     * - resumeFile: PDF/DOC/DOCX to parse server-side (merged with candidateData)
     * - jobId: associate candidate with job (auto-scores if enabled)
     *
     * At least one of candidateData or resumeFile must be provided.
     */
    async create(candidateData, resumeFile, jobId) {
        this.deps.ensureAuthenticated();
        try {
            const response = await this.deps.makeApiCallWithRetry(() => {
                if (resumeFile) {
                    return this.api.createCandidateFromResume(resumeFile, jobId, candidateData);
                }
                return this.api.createCandidateUnified({ candidateData, jobId });
            });
            const candidate = response.candidate;
            this.deps.telemetryService.trackCandidateCreated({
                sourceType: resumeFile ? CandidateCreationSourceType.Pdf : CandidateCreationSourceType.Manual,
                hasResume: !!resumeFile || !!response.resumeMeta,
            });
            this.cache.updateCandidate(candidate);
            this.events.emit(AppEventType.CANDIDATE_CREATED, { candidate, timestamp: new Date() });
            return response;
        }
        catch (error) {
            this.deps.handleError('createCandidate', this.deps.toError(error));
            throw error;
        }
    }
    async delete(candidateIds) {
        this.deps.ensureAuthenticated();
        try {
            const response = await this.deps.makeApiCallWithRetry(() => this.api.deleteCandidates(candidateIds));
            for (const id of response.deleted) {
                this.cache.removeCandidate(id);
            }
            if (response.deleted.length > 0) {
                this.events.emit(AppEventType.CANDIDATE_DELETED, { candidateIds: response.deleted, timestamp: new Date() });
            }
            return response;
        }
        catch (error) {
            this.deps.handleError('deleteCandidates', this.deps.toError(error));
            throw error;
        }
    }
    /**
     * Resolve a `processingStage = 'conflict'` placeholder. The server either
     * deletes the placeholder (`keep_existing`) or merges it into the existing
     * candidate and deletes the placeholder (`keep_new`). Cache patches arrive
     * via `candidate_updated` + `candidate_deleted` notifications, so we only
     * need to call the endpoint here.
     */
    async resolveConflict(candidateId, jobId, action) {
        this.deps.ensureAuthenticated();
        try {
            await this.deps.makeApiCallWithRetry(() => this.api.resolveCandidateConflict(candidateId, { jobId, action }));
        }
        catch (error) {
            this.deps.handleError('resolveCandidateConflict', this.deps.toError(error));
            throw error;
        }
    }
    /**
     * Batch create candidates from resume files in parallel. Each file goes
     * through the unified create endpoint (parse + create + auto-score).
     */
    async createFromResumes(resumeFiles, jobId) {
        this.deps.ensureAuthenticated();
        const results = await Promise.allSettled(resumeFiles.map((file) => this.create(undefined, file, jobId)));
        const successful = [];
        const failures = [];
        results.forEach((result, i) => {
            if (result.status === 'fulfilled') {
                successful.push(result.value.candidate);
            }
            else {
                const file = resumeFiles[i];
                failures.push({
                    error: `${file.name}: ${result.reason instanceof Error ? result.reason.message : 'Failed to create candidate'}`,
                });
            }
        });
        return { successful, failures };
    }
    // ===============================
    // Scoring
    // ===============================
    async score(candidateId, jobId) {
        this.deps.ensureAuthenticated();
        try {
            const response = await this.deps.makeApiCallWithRetry(() => this.api.scoreCandidate(candidateId, jobId));
            const candidate = response.candidate;
            const jobApplication = candidate.applications[jobId];
            this.deps.telemetryService.trackCandidateScored({
                candidateId: candidate.id,
                score: jobApplication.score,
                scoringMethod: 'ai_analysis'
            });
            this.cache.updateCandidate(candidate);
            this.emitUserDataIfPresent(response.updatedUserData);
            this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate, timestamp: new Date() });
            return candidate;
        }
        catch (error) {
            this.deps.handleError('scoreCandidate', this.deps.toError(error));
            throw error;
        }
    }
    async updateScore(candidateId, jobId, newScore, reason, newRecommendation) {
        this.deps.ensureAuthenticated();
        try {
            const currentCandidate = this.cache.getCandidate(candidateId);
            if (!currentCandidate.applications?.[jobId]) {
                throw new Error(`Candidate ${candidateId} has no application for job ${jobId}`);
            }
            const oldScore = currentCandidate.applications[jobId].score || 0;
            const response = await this.deps.makeApiCallWithRetry(() => this.api.updateCandidateScore(candidateId, jobId, newScore, reason, newRecommendation));
            const candidate = response.candidate;
            this.deps.telemetryService.trackCandidateScoreChanged({ candidateId, oldScore, newScore, reason, recommendation: newRecommendation });
            this.cache.updateCandidate(candidate);
            this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate, timestamp: new Date() });
            return candidate;
        }
        catch (error) {
            this.deps.handleError('updateCandidateScore', this.deps.toError(error));
            throw error;
        }
    }
    // ===============================
    // Hiring status
    // ===============================
    /** Optimistically merge a field-level delta into a cached application (replaced,
     *  not mutated, so identity-keyed memos invalidate) and emit CANDIDATE_UPDATED. */
    patchApplication(current, jobId, patch) {
        const app = current.applications?.[jobId];
        if (!app)
            return;
        const updated = {
            ...current,
            applications: { ...current.applications, [jobId]: { ...app, ...patch } },
        };
        this.cache.updateCandidate(updated);
        this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate: updated, timestamp: new Date() });
    }
    /** Restore a pre-optimistic snapshot after a failed mutation. */
    revertCandidate(previous) {
        this.cache.updateCandidate(previous);
        this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate: previous, timestamp: new Date() });
    }
    async updateHiringStatus(candidateId, jobId, status, remark) {
        this.deps.ensureAuthenticated();
        // 1) Local model change, reflected in the UI immediately.
        const previous = this.cache.getCandidate(candidateId);
        const optimistic = previous.applications?.[jobId] !== undefined;
        if (optimistic)
            this.patchApplication(previous, jobId, { hiringStatus: status });
        try {
            // 2) Server update — the authoritative candidate comes back in the response.
            const response = await this.deps.makeApiCallWithRetry(() => this.api.updateCandidateHiringStatus(candidateId, jobId, status, remark));
            const candidate = response.candidate;
            this.cache.updateCandidate(candidate);
            this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate, timestamp: new Date() });
            return candidate;
        }
        catch (error) {
            if (optimistic)
                this.revertCandidate(previous);
            this.deps.handleError('updateCandidateHiringStatus', this.deps.toError(error));
            throw error;
        }
    }
    // ===============================
    // Set candidate state (pipeline stage move)
    // ===============================
    async setCandidateState(candidateId, jobId, state) {
        this.deps.ensureAuthenticated();
        try {
            const response = await this.deps.makeApiCallWithRetry(() => this.api.setCandidateState(candidateId, jobId, state));
            const candidate = response.candidate;
            this.cache.updateCandidate(candidate);
            this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate, timestamp: new Date() });
            return candidate;
        }
        catch (error) {
            this.deps.handleError('setCandidateState', this.deps.toError(error));
            throw error;
        }
    }
    async moveCandidateStage(candidateId, jobId, stageId) {
        this.deps.ensureAuthenticated();
        try {
            const response = await this.deps.makeApiCallWithRetry(() => this.api.updateCandidateJobData({ candidateId, jobId, updates: { stageId } }));
            const candidate = response.candidate;
            this.cache.updateCandidate(candidate);
            this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate, timestamp: new Date() });
            return candidate;
        }
        catch (error) {
            this.deps.handleError('moveCandidateStage', this.deps.toError(error));
            throw error;
        }
    }
    // ===============================
    // Accept candidate
    // ===============================
    async acceptCandidate(candidateId, jobId) {
        this.deps.ensureAuthenticated();
        try {
            const response = await this.deps.makeApiCallWithRetry(() => this.api.acceptCandidate(candidateId, jobId));
            const candidate = response.candidate;
            this.cache.updateCandidate(candidate);
            this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate, timestamp: new Date() });
            return candidate;
        }
        catch (error) {
            this.deps.handleError('acceptCandidate', this.deps.toError(error));
            throw error;
        }
    }
    async rejectCandidate(candidateId, jobId, reason) {
        this.deps.ensureAuthenticated();
        try {
            const response = await this.deps.makeApiCallWithRetry(() => this.api.rejectCandidate(candidateId, jobId, reason));
            const candidate = response.candidate;
            this.cache.updateCandidate(candidate);
            this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate, timestamp: new Date() });
            return candidate;
        }
        catch (error) {
            this.deps.handleError('rejectCandidate', this.deps.toError(error));
            throw error;
        }
    }
    async restoreCandidate(candidateId, jobId) {
        this.deps.ensureAuthenticated();
        try {
            const response = await this.deps.makeApiCallWithRetry(() => this.api.restoreCandidate(candidateId, jobId));
            const candidate = response.candidate;
            this.cache.updateCandidate(candidate);
            this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate, timestamp: new Date() });
            return candidate;
        }
        catch (error) {
            this.deps.handleError('restoreCandidate', this.deps.toError(error));
            throw error;
        }
    }
    // ===============================
    // Send to Offer
    // ===============================
    async sendToOffer(candidateId, jobId) {
        this.deps.ensureAuthenticated();
        // 1) Local model change (state + offer-stage advance) reflected immediately.
        const previous = this.cache.getCandidate(candidateId);
        const priorApp = previous.applications?.[jobId];
        const job = this.cache.getJob(jobId);
        if (priorApp && job) {
            this.patchApplication(previous, jobId, {
                state: CandidateState.OfferReady,
                stageId: advanceStageForCapability(priorApp.stageId, StageCapability.Offer, job.pipelineTemplate),
            });
        }
        try {
            const response = await this.deps.makeApiCallWithRetry(() => this.api.sendToOffer(candidateId, jobId));
            const candidate = response.candidate;
            this.cache.updateCandidate(candidate);
            this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate, timestamp: new Date() });
            return candidate;
        }
        catch (error) {
            if (priorApp && job)
                this.revertCandidate(previous);
            this.deps.handleError('sendToOffer', this.deps.toError(error));
            throw error;
        }
    }
    // ===============================
    // Tags
    // ===============================
    async updateTags(candidateId, jobId, tags) {
        this.deps.ensureAuthenticated();
        try {
            // Optimistic update: patch the cache immediately
            const current = this.cache.getCandidate(candidateId);
            if (current.applications?.[jobId]) {
                const optimistic = {
                    ...current,
                    applications: {
                        ...current.applications,
                        [jobId]: { ...current.applications[jobId], tags },
                    },
                };
                this.cache.updateCandidate(optimistic);
                this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate: optimistic, timestamp: new Date() });
            }
            const response = await this.deps.makeApiCallWithRetry(() => this.api.updateCandidateJobData({ candidateId, jobId, updates: { tags } }));
            const candidate = response.candidate;
            // Only emit again if the server response differs from our optimistic update
            const serverTags = candidate.applications?.[jobId]?.tags;
            const tagsChanged = !serverTags || !tags || serverTags.length !== tags.length ||
                serverTags.some((t, i) => t !== tags[i]);
            if (tagsChanged) {
                this.cache.updateCandidate(candidate);
                this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate, timestamp: new Date() });
            }
            return candidate;
        }
        catch (error) {
            // Revert optimistic update on failure
            this.deps.handleError('updateCandidateTags', this.deps.toError(error));
            throw error;
        }
    }
    // ===============================
    // Per-candidate qualification questions
    // ===============================
    async updateQuestions(candidateId, jobId, questions) {
        this.deps.ensureAuthenticated();
        // Optimistic update: patch the cache immediately so the drawer
        // re-renders without waiting for the round-trip. Server response
        // (with sent-question protection applied) overrides on completion.
        const current = this.cache.getCandidate(candidateId);
        const priorApp = current.applications?.[jobId];
        if (priorApp) {
            const optimistic = {
                ...current,
                applications: {
                    ...current.applications,
                    [jobId]: { ...priorApp, qualificationQuestions: questions },
                },
            };
            this.cache.updateCandidate(optimistic);
            this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate: optimistic, timestamp: new Date() });
        }
        try {
            const response = await this.deps.makeApiCallWithRetry(() => this.api.updateCandidateJobData({
                candidateId,
                jobId,
                updates: { qualificationQuestions: questions },
            }));
            const candidate = response.candidate;
            this.cache.updateCandidate(candidate);
            this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate, timestamp: new Date() });
            return candidate;
        }
        catch (error) {
            // Revert optimistic update by re-patching with the pre-edit state.
            if (priorApp) {
                this.cache.updateCandidate(current);
                this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate: current, timestamp: new Date() });
            }
            this.deps.handleError('updateCandidateQuestions', this.deps.toError(error));
            throw error;
        }
    }
    async resetQuestionsToJob(candidateId, jobId) {
        this.deps.ensureAuthenticated();
        // Optimistic update: clearing override = keep only immutable entries
        // (sent or answered). Must match server's `isImmutable` rule in
        // `/candidates/update-job-data` to avoid flicker on round-trip.
        const current = this.cache.getCandidate(candidateId);
        const priorApp = current.applications?.[jobId];
        if (priorApp) {
            const immutableOnly = (priorApp.qualificationQuestions ?? [])
                .filter(q => q.sentTimeUtc !== undefined || q.response !== undefined);
            const optimistic = {
                ...current,
                applications: {
                    ...current.applications,
                    [jobId]: { ...priorApp, qualificationQuestions: immutableOnly },
                },
            };
            this.cache.updateCandidate(optimistic);
            this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate: optimistic, timestamp: new Date() });
        }
        try {
            const response = await this.deps.makeApiCallWithRetry(() => this.api.updateCandidateJobData({
                candidateId,
                jobId,
                updates: {},
                resetQuestionsToJobDefaults: true,
            }));
            const candidate = response.candidate;
            this.cache.updateCandidate(candidate);
            this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate, timestamp: new Date() });
            return candidate;
        }
        catch (error) {
            if (priorApp) {
                this.cache.updateCandidate(current);
                this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate: current, timestamp: new Date() });
            }
            this.deps.handleError('resetCandidateQuestionsToJob', this.deps.toError(error));
            throw error;
        }
    }
    // ===============================
    // Job association
    // ===============================
    async associateWithJob(candidateId, jobId, reason) {
        this.deps.ensureAuthenticated();
        try {
            const response = await this.deps.makeApiCallWithRetry(() => this.api.associateCandidateWithJob({ candidateId, jobId, reason }));
            if (response.candidate) {
                this.cache.updateCandidate(response.candidate);
                this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate: response.candidate, timestamp: new Date() });
            }
            return response.candidate;
        }
        catch (error) {
            this.deps.handleError('associateCandidateWithJob', this.deps.toError(error));
            throw error;
        }
    }
    // ===============================
    // Interview
    // ===============================
    async inviteToInterview(candidateId, jobId) {
        this.deps.ensureAuthenticated();
        try {
            const response = await this.deps.makeApiCallWithRetry(() => this.api.inviteCandidateToInterview(candidateId, jobId));
            const cached = this.cache.getCandidate(candidateId);
            const prior = cached?.applications[jobId];
            const job = this.cache.getJob(jobId);
            if (cached && prior && job) {
                // Replace (not mutate) the application so identity-keyed memos invalidate.
                const nextApp = {
                    ...prior,
                    state: CandidateState.InterviewSchedulingSent,
                    // Advance to the interview stage from the candidate's current position
                    // (a repeated round moves to the next interview stage).
                    stageId: advanceStageForCapability(prior.stageId, StageCapability.Interview, job.pipelineTemplate),
                };
                if (response.proposedTimeSlots?.length) {
                    nextApp.interviewInvitation = {
                        ...(prior.interviewInvitation ?? { sent: false }),
                        sent: true,
                        sentTimeUtc: nowUtc(),
                        timeSlots: response.proposedTimeSlots,
                    };
                }
                const updated = { ...cached, applications: { ...cached.applications, [jobId]: nextApp } };
                this.cache.updateCandidate(updated);
                this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate: updated, timestamp: new Date() });
            }
        }
        catch (error) {
            this.deps.handleError('inviteCandidateToInterview', this.deps.toError(error));
            throw error;
        }
    }
    async cancelInterview(eventId, reason) {
        await this.api.cancelInterview(eventId, reason);
    }
    /**
     * "Ask candidate to repick" — cancels the current slot and triggers a
     * re-engagement SMS. Server returns the updated candidate which patches
     * the local cache.
     */
    async requestReschedule(candidateId, jobId, reason) {
        this.deps.ensureAuthenticated();
        try {
            const response = await this.deps.makeApiCallWithRetry(() => this.api.requestReschedule({ candidateId, jobId, reason }));
            const candidate = response.candidate;
            this.cache.updateCandidate(candidate);
            this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate, timestamp: new Date() });
            return candidate;
        }
        catch (error) {
            this.deps.handleError('requestReschedule', this.deps.toError(error));
            throw error;
        }
    }
    /**
     * "Set new time" — recruiter has agreed a new slot with the candidate.
     * Server cancels the old slot, schedules the new one, sends a reschedule
     * notice SMS, and returns the updated candidate delta.
     */
    async rescheduleInterview(candidateId, jobId, newSlot, rescheduleMessage, opts) {
        this.deps.ensureAuthenticated();
        try {
            const response = await this.deps.makeApiCallWithRetry(() => this.api.rescheduleInterview({
                candidateId,
                jobId,
                newSlot,
                rescheduleMessage,
                touchpointOverrides: opts?.touchpointOverrides,
                reminders: opts?.reminders,
            }));
            const candidate = response.candidate;
            this.cache.updateCandidate(candidate);
            this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate, timestamp: new Date() });
            return candidate;
        }
        catch (error) {
            this.deps.handleError('rescheduleInterview', this.deps.toError(error));
            throw error;
        }
    }
    // ===============================
    // Qualification questions
    // ===============================
    async startQuestions(candidateId, jobId) {
        this.deps.ensureAuthenticated();
        try {
            const result = await this.deps.makeApiCallWithRetry(() => this.api.startCandidateQuestions(candidateId, jobId));
            return result.nextQuestion;
        }
        catch (error) {
            this.deps.handleError('startCandidateQuestions', this.deps.toError(error));
            throw error;
        }
    }
    async getQuestions(candidateId, jobId) {
        this.deps.ensureAuthenticated();
        try {
            return await this.deps.makeApiCallWithRetry(() => this.api.getCandidateQuestions(candidateId, jobId));
        }
        catch (error) {
            this.deps.handleError('getCandidateQuestions', this.deps.toError(error));
            throw error;
        }
    }
    /**
     * Send a qualification SMS to a candidate.
     *
     * Returns the server's tagged response — callers must inspect `result.status`:
     *  - `'sent'`             → message went out (or carrier-failed); local cache is patched.
     *  - `'action_required'` → server gated the send; caller passes the action to
     *                          PhoneVerificationGate which opens the right dialog.
     *                          When the user resolves the gate the server resumes
     *                          the original send automatically — no client retry.
     */
    async sendMessage(candidateId, jobId, transport) {
        this.deps.ensureAuthenticated();
        try {
            const result = await this.deps.makeApiCallWithRetry(() => this.api.sendMessageToCandidate(candidateId, jobId, transport));
            if (result.status === 'sent') {
                // Patch local cache with the delta returned by the server — no refetch.
                // Org credit balance is updated separately via the ORG_UPDATED notification.
                const cached = this.cache.getCandidate(candidateId);
                const prior = cached?.applications[jobId];
                const job = this.cache.getJob(jobId);
                if (cached && prior && job) {
                    // Replace (not mutate) the application so identity-keyed memos invalidate.
                    const updated = {
                        ...cached,
                        applications: {
                            ...cached.applications,
                            [jobId]: {
                                ...prior,
                                state: result.candidateState,
                                stageId: advanceStageForCapability(prior.stageId, StageCapability.SmsScreening, job.pipelineTemplate),
                                qualificationQuestions: result.qualificationQuestions,
                            },
                        },
                    };
                    this.cache.updateCandidate(updated);
                    this.events.emit(AppEventType.CANDIDATE_UPDATED, { candidate: updated, timestamp: new Date() });
                }
                if (!result.delivered) {
                    this.deps.handleError('sendMessageToCandidate', new Error(`Message delivery failed for candidate ${candidateId}`));
                }
            }
            // For 'action_required' there are no chat side-effects to mirror — the
            // server has only persisted a PendingSend record. The caller surfaces
            // the action to the user via PhoneVerificationGate.
            return result;
        }
        catch (error) {
            this.deps.handleError('sendMessageToCandidate', this.deps.toError(error));
            throw error;
        }
    }
    // ===============================
    // Internal helpers
    // ===============================
    emitUserDataIfPresent(updatedUserData) {
        if (updatedUserData) {
            const event = { userData: updatedUserData, timestamp: new Date() };
            this.events.emit(AppEventType.USERDATA_UPDATED, event);
        }
    }
}
